import { Check, X } from "lucide-react";
import { type FC } from "react";

import SortArrow from "@/assets/images/SortArrow";
import SortArrowAsc from "@/assets/images/SortArrowAsc";
import SortArrowDesc from "@/assets/images/SortArrowDesc";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { commonIconProps12 } from "@/utils/constants";
import { SortDirection, type SortType } from "@/utils/hooks/useTableParams";

import type { TableProps } from "./types";

interface TableHeadCellSortProps extends TableProps {
  sortOrder?: SortType;
  onSortChange?: (direction: SortType) => void;
  field?: string;
  activeField?: string | unknown;
  useUpperCase?: boolean;
  isInvisible?: boolean;
}

const sortOptions = (useUpperCase?: boolean) => [
  {
    label: useUpperCase ? "ASC" : "Asc",
    value: useUpperCase ? "ASC" : SortDirection.ASC,
    Icon: SortArrowAsc,
  },
  {
    label: useUpperCase ? "DESC" : "Desc",
    value: useUpperCase ? "DESC" : SortDirection.DESC,
    Icon: SortArrowDesc,
  },
];

const TableHeadCellSort: FC<TableHeadCellSortProps> = ({
  children,
  classes,
  onSortChange,
  sortOrder,
  field,
  activeField,
  useUpperCase,
  isInvisible = false,
}) => {
  const handleSort = (direction: SortType) => {
    if (!onSortChange) return;

    if (!direction) {
      onSortChange(null);
      return;
    }

    const normalized = useUpperCase
      ? (direction.toUpperCase() as SortType)
      : (direction.toLowerCase() as SortType);

    onSortChange(normalized);
  };

  const SortArrowIcon = (() => {
    if (activeField !== field) return SortArrow;
    const normalizedOrder = useUpperCase
      ? sortOrder?.toUpperCase()
      : sortOrder?.toLowerCase();
    switch (normalizedOrder) {
      case "asc":
        return SortArrowAsc;
      case "desc":
        return SortArrowDesc;
      default:
        return SortArrow;
    }
  })();

  return (
    <TableHead
      className={cn(
        "py-3.5 px-3 text-left text-xs text-gray-500 border-b border-gray-200",
        classes
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              "flex items-center gap-2 cursor-pointer text-left",
              isInvisible && "invisible"
            )}
            data-testid="table-head-cell-sort-button"
          >
            <span>{children}</span>
            <SortArrowIcon classes="shrink-0" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start">
          {sortOptions(useUpperCase).map(({ label, value, Icon }) => (
            <DropdownMenuItem
              key={value}
              onClick={() => handleSort(value as SortType)}
            >
              <Icon classes="mr-2 h-4 w-4 shrink-0" />
              {label}
              {sortOrder === value && <Check className="ml-2 h-4 w-4" />}
            </DropdownMenuItem>
          ))}
          {sortOrder && (
            <DropdownMenuItem onClick={() => handleSort(null)}>
              <X {...commonIconProps12} color="#AFAFAF" className="mr-2" />
              Reset
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </TableHead>
  );
};

export default TableHeadCellSort;
